import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Home, DollarSign, Megaphone, Award, BadgeDollarSign, UserCheck, Clock } from "lucide-react";
import "./Properties.css";

const baseUrl = process.env.REACT_APP_BASE_URL;
const API_URL = `${baseUrl}/all/property/list/all/purpose`;
const PROPERTY_TYPES = ["Villa", "Flat", "House"];

const fetchProperties = async (type) => {
  try {
    const response = await fetch(API_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        page_number: 1,
        page_size: 1,
        order_by: "ASC",
        type,
      }),
    });
    return await response.json();
  } catch (error) {
    console.error("Error fetching properties:", error);
    return null;
  }
};

const SERVICES = [
  {
    icon: Home,
    title: "Looking to Buy?",
    text: "Find your dream property with our detailed listings and virtual tours.",
    btn: "Search Properties",
    action: "search",
  },
  {
    icon: DollarSign,
    title: "Want to Sell?",
    text: "List your property and reach thousands of potential buyers.",
    btn: "List Your Property",
    action: "dashboard",
  },
  {
    icon: Megaphone,
    title: "Want to Promote?",
    text: "Boost your property visibility with premium promotion services.",
    btn: "Promote Now",
    action: "dashboard",
  },
];

const WHY_ITEMS = [
  { icon: Award,           title: "Trusted Experience", text: "Over 15 years of excellence in real estate services." },
  { icon: BadgeDollarSign, title: "Best Deals",         text: "Competitive prices and exclusive property offers." },
  { icon: UserCheck,       title: "Expert Agents",      text: "Professional and dedicated real estate agents." },
  { icon: Clock,           title: "24/7 Support",       text: "Round-the-clock customer service and support." },
];

const PropertiesPage = () => {
  const [properties, setProperties] = useState([]);
  const [assetUrl, setAssetUrl] = useState("");
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const isLoggedIn = () => localStorage.getItem("authToken");

  useEffect(() => {
    const loadProperties = async () => {
      try {
        const fetched = await Promise.all(PROPERTY_TYPES.map(fetchProperties));
        const valid = fetched.filter((d) => d && d.statusType === "SUCCESS");
        if (valid.length > 0) {
          setAssetUrl(valid[0].assetUrl);
          setProperties(valid.flatMap((d) => d.properties));
        }
      } catch (error) {
        console.error("Error loading properties:", error);
      } finally {
        setLoading(false);
      }
    };
    loadProperties();
  }, []);

  const handleNav = () => {
    navigate(isLoggedIn() ? "/customer/myspace" : "/login");
  };

  const formatPrice = (amount) =>
    new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(amount);

  return (
    <main className="props-page">

      <section className="props-page__section props-page__section--alt">
        <div className="props-page__container">
          <div className="props-page__section-header">
            <span className="props-page__section-label">Handpicked for you</span>
            <h2 className="props-page__section-title">
              Featured <em>Properties</em>
            </h2>
          </div>

          {loading ? (
            <div className="props-page__empty">Loading properties…</div>
          ) : properties.length === 0 ? (
            <div className="props-page__empty">No properties found.</div>
          ) : (
            <div className="props-page__grid">
              {properties.map((property) => (
                <div className="props-page__card" key={property.id}>
                  <div className="props-page__card-img-wrap">
                    <img
                      className="props-page__card-img"
                      src={`${assetUrl}${property.images[0]?.image_url}`}
                      alt={property.title}
                    />
                    {property.type && (
                      <span className="props-page__card-type">{property.type}</span>
                    )}
                  </div>
                  <div className="props-page__card-body">
                    <h3 className="props-page__card-title">{property.title}</h3>
                    <p className="props-page__card-address">
                      {property.address}, {property.city?.name}, {property.state?.name}
                    </p>
                    <div className="props-page__card-footer">
                      <span className="props-page__card-price">
                        {formatPrice(property.budget)}
                      </span>
                      <button
                        className="props-page__card-btn"
                        onClick={() => handleNav(property.id)}
                      >
                        View Details
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="props-page__section">
        <div className="props-page__container">
          <div className="props-page__section-header">
            <span className="props-page__section-label">What we offer</span>
            <h2 className="props-page__section-title">
              Our <em>Services</em>
            </h2>
          </div>
          <div className="props-page__services-grid">
            {SERVICES.map(({ icon: Icon, title, text, btn, action }) => (
              <div className="props-page__service-card" key={title}>
                <div className="props-page__service-icon">
                  <Icon size={22} />
                </div>
                <h3 className="props-page__service-title">{title}</h3>
                <p className="props-page__service-text">{text}</p>
                <button className="props-page__service-btn" onClick={handleNav}>
                  {btn}
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="props-page__section props-page__section--alt">
        <div className="props-page__container">
          <div className="props-page__section-header">
            <span className="props-page__section-label">Our strengths</span>
            <h2 className="props-page__section-title">
              Why Choose <em>Us</em>
            </h2>
          </div>
          <div className="props-page__why-grid">
            {WHY_ITEMS.map(({ icon: Icon, title, text }, i) => (
              <div className="props-page__why-item" key={title}>
                <span className="props-page__why-number">0{i + 1}</span>
                <div className="props-page__why-icon">
                  <Icon size={20} />
                </div>
                <h4 className="props-page__why-title">{title}</h4>
                <p className="props-page__why-text">{text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

    </main>
  );
};

export default PropertiesPage;
